import { useLocation, useNavigate, Link } from "react-router-dom";
import { useContent } from "@/i18n";
import { firm, telHref } from "@/config/firm.config";
import { scrollToHash } from "@/hooks/useLenis";
import { Logo } from "./Logo";

const LINKS = ["home", "about", "practice", "advocates", "contact"];

export const Footer = () => {
  const { t } = useContent();
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";
  const go = (hash) => (e) => {
    e.preventDefault();
    if (onHome) {
      scrollToHash(hash);
    } else {
      navigate(`/${hash}`);
    }
  };
  return (
    <footer data-testid="footer" className="relative border-t border-gold/20 bg-navy-900">
      <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-12">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Logo />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-ink">{firm.tagline} · {firm.city}, {firm.state}</p>
            <p className="mt-3 text-xs text-ink/60">{t.footer.bci}</p>
          </div>
          <nav aria-label="Footer" className="md:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{t.footer.quickLinks}</p>
            <ul className="mt-5 space-y-3">
              {LINKS.map((k) => (
                <li key={k}>
                  <a href={`#${k}`} onClick={go(`#${k}`)} data-testid={`footer-link-${k}`} className="focus-ring text-sm text-ink transition-colors duration-300 hover:text-gold-light">
                    {t.nav[k]}
                  </a>
                </li>
              ))}
              <li>
                <Link to="/careers" data-testid="footer-link-careers" className="focus-ring text-sm text-ink transition-colors duration-300 hover:text-gold-light">
                  {t.nav.careers}
                </Link>
              </li>
            </ul>
          </nav>
          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{t.footer.office}</p>
            <address className="mt-5 text-sm not-italic leading-relaxed text-ink">
              {firm.address.line1}
              <br />
              {firm.address.line2}, {firm.address.city}
              <br />
              <span className="text-ink/70">{firm.address.landmark}</span>
              <br />
              {firm.address.state} – {firm.address.pin}
            </address>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{t.footer.contact}</p>
            <ul className="mt-5 space-y-2 text-sm text-ink">
              {firm.advocates.map((a) => (
                <li key={a.id}>
                  <a href={telHref(a.phone)} data-testid={`footer-phone-${a.id}`} className="focus-ring hover:text-gold-light">{a.phone}</a>
                </li>
              ))}
              <li>
                <a href={`mailto:${firm.email}`} data-testid="footer-email" className="focus-ring break-all hover:text-gold-light">{firm.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div data-testid="footer-disclaimer" className="mt-16 border border-gold/40 bg-navy-800/60 p-6 md:p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{t.footer.disclaimerTitle}</p>
          <p className="mt-4 text-sm leading-relaxed text-paper/85">{t.footer.disclaimer}</p>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-white/10 pt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-copyright">© 2026 {firm.name}. {t.footer.rights}</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a
              href="/privacy-policy"
              data-testid="footer-privacy-link"
              className="focus-ring transition-colors duration-300 hover:text-gold-light"
            >
              {t.footer.privacy}
            </a>
            <span aria-hidden="true" className="hidden h-3 w-px bg-ink/20 sm:inline-block" />
            <p>{firm.city} · {firm.state} · {firm.address.country}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
